import React, { useState, useEffect, useRef } from 'react';
import './PaymentModal.css';
import { paymentService } from '../invoice/services/paymentService';
import { getUserData, getOrgGuid } from '../../shared/utils/authStorage';
import PaymentSuccess from './PaymentSuccess';
import PaymentFailure from './PaymentFailure';
import { useSnackbar } from '../../contexts/SnackbarContext';

const loadRazorpay = () =>
    new Promise((resolve) => {
        if (window.Razorpay) return resolve(true);
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });

const PaymentModal = ({ invoice, onClose, onSuccess }) => {
    const { showSnackbar } = useSnackbar();
    const [methods, setMethods] = useState(null); // null = loading, [] = none
    const [tab, setTab] = useState(null);
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [manualResult, setManualResult] = useState(null);
    const [successData, setSuccessData] = useState(null);
    const [failureReason, setFailureReason] = useState(null);
    const rzpRef = useRef(null);
    const paymentDoneRef = useRef(false);
    const pendingFailureRef = useRef(null);

    useEffect(() => {
        const orgGuid = getOrgGuid();
        if (!orgGuid) { setMethods([]); return; }
        paymentService.getOrgPaymentMethods(orgGuid)
            .then((res) => {
                const data = res.data || [];
                const list = Array.isArray(data) ? data : (data.data || []);
                // Only care about razorpay and manual
                const hasRazorpay = list.some(m => m.method_configuration?.name === 'razorpay' && m.is_active);
                const hasManual = list.some(m => m.method_configuration?.name === 'manual_bank' && m.is_active);
                const available = [];
                if (hasRazorpay) available.push('razorpay');
                if (hasManual) available.push('manual');
                setMethods(available);
                setTab(available[0] || null);
            })
            .catch(() => { setMethods([]); });
    }, []);

    const extractErrorMessage = (err, fallback) => {
        const data = err?.response?.data;
        if (data?.errors?.length) return data.errors.map(e => e.message).join(' ');
        if (Array.isArray(data)) return data.map(e => e.message).join(' ');
        return data?.message || fallback;
    };

    const handleManualSubmit = async () => {
        if (!notes.trim()) { showSnackbar('Payment reference / notes cannot be blank.'); return; }
        setLoading(true);
        try {
            const res = await paymentService.initiateManual(invoice.id, notes);
            const data = res.data.data;
            setManualResult(data);
            onSuccess({ type: 'manual', data });
        } catch (err) {
            showSnackbar(extractErrorMessage(err, 'Manual payment submission failed.'));
        } finally {
            setLoading(false);
        }
    };

    const handleRazorpaySubmit = async () => {
        setLoading(true);
        paymentDoneRef.current = false;
        pendingFailureRef.current = null;
        try {
            const loaded = await loadRazorpay();
            if (!loaded) throw new Error('Failed to load Razorpay SDK.');

            const initRes = await paymentService.initiateGateway('razorpay', invoice.id);
            const { payment_guid, order_id, public_key, amount, currency } = initRes.data.data;

            const userData = getUserData();
            const options = {
                key: public_key,
                amount: Math.round(parseFloat(amount)),
                currency: currency || 'INR',
                order_id,
                name: userData?.organization_unit?.org_name || 'Tykven',
                description: `Invoice ${invoice.invoiceNo}`,
                handler: async (response) => {
                    paymentDoneRef.current = true;
                    try {
                        await paymentService.verifyRazorpay(
                            payment_guid,
                            response.razorpay_payment_id,
                            response.razorpay_signature,
                        );
                        setSuccessData({ amount, currency });
                    } catch {
                        setFailureReason('Payment verification failed. Please contact support.');
                        setLoading(false);
                    }
                },
                modal: {
                    ondismiss: () => {
                        if (!paymentDoneRef.current) {
                            setFailureReason(pendingFailureRef.current || 'Payment was not completed.');
                        }
                        setLoading(false);
                    },
                },
            };

            const rzp = new window.Razorpay(options);
            rzpRef.current = rzp;
            rzp.on('payment.failed', (resp) => {
                pendingFailureRef.current = resp.error?.description || 'Payment failed.';
            });
            rzp.open();
        } catch (err) {
            showSnackbar(extractErrorMessage(err, err.message || 'Payment initialisation failed.'));
            setLoading(false);
        }
    };

    if (successData) {
        return (
            <PaymentSuccess
                amount={successData.amount}
                currency={successData.currency}
                onClose={() => { onSuccess({ type: 'razorpay' }); onClose(); }}
            />
        );
    }

    if (failureReason) {
        return (
            <PaymentFailure
                reason={failureReason}
                onClose={() => setFailureReason(null)}
            />
        );
    }

    return (
        <div className="pm-overlay" onClick={onClose}>
            <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
                <div className="pm-header">
                    <span>Pay Invoice — {invoice.invoiceNo}</span>
                    <button className="pm-close" onClick={onClose}>✕</button>
                </div>

                <div className="pm-amount">Amount Due: {invoice.outstandingAmount ?? invoice.amount}</div>

                {methods === null ? (
                    <div className="pm-body"><p className="pm-hint">Loading payment methods…</p></div>
                ) : methods.length === 0 ? (
                    <div className="pm-body"><p className="pm-hint">No payment methods are currently available. Please contact support.</p></div>
                ) : (
                    <div className="pm-body">
                        <div className="pm-method-list">
                            {methods.includes('razorpay') && (
                                <button
                                    className={`pm-method-item ${tab === 'razorpay' ? 'active' : ''}`}
                                    onClick={() => setTab('razorpay')}
                                >
                                    <span className="pm-method-icon">💳</span>
                                    <span className="pm-method-info">
                                        <span className="pm-method-name">Razorpay</span>
                                        <span className="pm-method-desc">UPI, Card, Net Banking</span>
                                    </span>
                                    <span className="pm-method-arrow">›</span>
                                </button>
                            )}
                            {methods.includes('manual') && (
                                <button
                                    className={`pm-method-item ${tab === 'manual' ? 'active' : ''}`}
                                    onClick={() => setTab('manual')}
                                >
                                    <span className="pm-method-icon">🏦</span>
                                    <span className="pm-method-info">
                                        <span className="pm-method-name">Manual Payment</span>
                                        <span className="pm-method-desc">Bank transfer, Cheque, Cash</span>
                                    </span>
                                    <span className="pm-method-arrow">›</span>
                                </button>
                            )}
                        </div>

                        {tab === 'razorpay' && (
                            <div className="pm-section">
                                <button
                                    className="pm-btn pm-btn-primary"
                                    onClick={handleRazorpaySubmit}
                                    disabled={loading}
                                >
                                    {loading ? 'Processing…' : 'Pay with Razorpay'}
                                </button>
                            </div>
                        )}

                        {tab === 'manual' && (
                            <div className="pm-section">
                                {manualResult ? (
                                    <div className="pm-success">
                                        <p>✅ Payment recorded successfully.</p>
                                        <p>Ref: <strong>{manualResult.payment_trans_number}</strong></p>
                                        <p>Status: <strong>{manualResult.payment_status}</strong></p>
                                        <p className="pm-hint">Admin will review and confirm or reject your payment.</p>
                                        <button className="pm-btn pm-btn-primary" onClick={onClose}>Close</button>
                                    </div>
                                ) : (
                                    <>
                                        <label className="pm-label">Payment Reference / Notes *</label>
                                        <textarea
                                            className="pm-textarea"
                                            placeholder="e.g. NEFT Reference: TXN88990011"
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            rows={3}
                                        />
                                        <button
                                            className="pm-btn pm-btn-primary"
                                            onClick={handleManualSubmit}
                                            disabled={loading}
                                        >
                                            {loading ? 'Submitting…' : 'Submit Manual Payment'}
                                        </button>
                                    </>
                                )}
                            </div>
                        )}


                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentModal;
